import React, { createContext, useContext, useState, useEffect } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const REVIEW_HIDDEN_PAGES = process.env.REACT_APP_REVIEW_HIDDEN_PAGES === 'true';

const SettingsContext = createContext({
  showRsvp: false,
  showRegistry: false,
  loading: true,
  error: null,
  refetchSettings: () => {},
});

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState({
    showRsvp: REVIEW_HIDDEN_PAGES,
    showRegistry: REVIEW_HIDDEN_PAGES,
  });
  const [loading, setLoading] = useState(!REVIEW_HIDDEN_PAGES);
  const [error, setError] = useState(null);

  const fetchSettings = async (retries = 2) => {
    try {
      setLoading(true);
      setError(null);

      // 15-second timeout accommodates serverless cold starts
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);

      const response = await fetch(`${BACKEND_URL}/api/settings`, {
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error('Failed to fetch settings');
      }
      const data = await response.json();
      setSettings({
        showRsvp: REVIEW_HIDDEN_PAGES || (data.show_rsvp ?? false),
        showRegistry: REVIEW_HIDDEN_PAGES || (data.show_registry ?? false),
      });
    } catch (err) {
      // Retry on cold-start timeouts / transient failures
      if (retries > 0) {
        await new Promise((r) => setTimeout(r, 1500));
        return fetchSettings(retries - 1);
      }
      console.error('Error fetching settings:', err);
      setError(err.message);
      // Keep defaults on error (hidden)
      setSettings({
        showRsvp: REVIEW_HIDDEN_PAGES,
        showRegistry: REVIEW_HIDDEN_PAGES,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (REVIEW_HIDDEN_PAGES) {
      setLoading(false);
      return;
    }
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = {
    showRsvp: settings.showRsvp,
    showRegistry: settings.showRegistry,
    loading,
    error,
    refetchSettings: fetchSettings,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export default SettingsContext;
